import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logoutFromSuite } from '@/lib/isosofts';
import { useAuthStore } from '@/stores/auth.store';

import { SuiteLanguageSwitcher } from './SuiteLanguageSwitcher';
import SuiteTools from './SuiteTools';
import BrandMark from '@/components/BrandMark';

// Profil resmi/şirket logosu için .isosofts.com'da paylaşılan auth_token cookie'sinden
// isosofts-api'ye self çağrısı (ESD'nin kendi user'ı isosofts id'lerini taşımayabilir).
const getSuiteToken = (): string =>
  document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=').slice(1).join('=') ?? '';

interface IsoAccount { id: string; avatarPath?: string }
interface IsoCompany { id: string; logoPath?: string }

// IsoSofts suite top navbar, branded for ESD — mirrors Architecture/Algebra so the
// module feels like part of the suite. Sticky full-width row above the app shell.
export function SuiteNavbar() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [menuOpen, setMenuOpen] = useState(false);

  const companyName = user?.organization?.name ?? '';
  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  const [isoAccount, setIsoAccount] = useState<IsoAccount | null>(null);
  const [isoCompany, setIsoCompany] = useState<IsoCompany | null>(null);

  useEffect(() => {
    const token = getSuiteToken();
    if (!token) return;
    Promise.allSettled([
      fetch(`https://isosofts.com/api/account/self?token=${token}`).then((r) => r.json()),
      fetch(`https://isosofts.com/api/company/self?token=${token}`).then((r) => r.json()),
    ]).then(([a, c]) => {
      if (a.status === 'fulfilled') setIsoAccount(a.value as IsoAccount);
      if (c.status === 'fulfilled') setIsoCompany(c.value as IsoCompany);
    });
  }, []);

  const initials = (companyName || userName || 'D').split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const navLinks = [
    { label: t('suite.home'), href: 'https://www.isosofts.com/#home' },
    { label: t('suite.services'), href: 'https://www.isosofts.com/#services' },
    { label: t('suite.about'), href: 'https://www.isosofts.com/#about' },
    { label: t('suite.contact'), href: 'https://www.isosofts.com/#contact' },
  ];

  const handleLogout = () => {
    clear();
    logoutFromSuite();
  };

  return (
    <>
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-blue-100 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left — brand + desktop links */}
            <div className="flex items-center space-x-8">
              <div className="flex items-center gap-2.5">
                <BrandMark size={32} className="shadow-sm" />
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                  Documentology
                </span>
              </div>

              <div className="hidden md:flex space-x-6">
                {navLinks.map(({ label, href }) => (
                  <a key={label} href={href} className="text-sm text-gray-600 hover:text-blue-600 transition-colors font-medium">
                    {label}
                  </a>
                ))}
              </div>
            </div>

            {/* Right — language + company/user + account + logout */}
            <div className="flex items-center gap-3">
              <SuiteTools />
              <SuiteLanguageSwitcher />

              {(companyName || userName) && (
                <div className="hidden md:flex items-center gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-3 py-1.5">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm shrink-0 overflow-hidden">
                    {isoCompany?.logoPath
                      ? <img src={`https://isosofts.com/api/company/logo/${isoCompany.id}`} alt="" className="w-full h-full object-cover" />
                      : <span className="text-white text-[10px] font-bold">{initials}</span>}
                  </div>
                  <div className="leading-tight">
                    {companyName && <p className="text-[12px] font-semibold text-blue-800 leading-none">{companyName}</p>}
                    {userName && <p className="text-[11px] text-blue-500 leading-none mt-0.5">{userName}</p>}
                  </div>
                </div>
              )}

              <a
                href="https://www.isosofts.com/profile"
                className="hidden md:flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all shadow-md hover:shadow-lg"
              >
                {isoAccount?.avatarPath
                  ? <img src={`https://isosofts.com/api/account/avatar/${isoAccount.id}`} alt="" className="w-5 h-5 rounded-full object-cover -ml-0.5" />
                  : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5 0-9 2.5-9 6v1h18v-1c0-3.5-4-6-9-6z" /></svg>}
                {t('suite.account')}
              </a>

              <button
                onClick={handleLogout}
                className="hidden md:flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 text-sm font-semibold px-4 py-2 rounded-xl hover:bg-red-100 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                {t('suite.logout')}
              </button>

              <button
                className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label={t('suite.toggleMenu')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile dropdown */}
          {menuOpen && (
            <div className="md:hidden border-t border-gray-100 mt-2.5 pt-2.5 pb-1 space-y-1">
              {navLinks.map(({ label, href }) => (
                <a key={label} href={href} className="block px-2 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                  {label}
                </a>
              ))}
              {(companyName || userName) && (
                <div className="px-2 py-1.5 text-[12px] text-blue-700 font-semibold">{companyName}{userName ? ` · ${userName}` : ''}</div>
              )}
              <a href="https://www.isosofts.com/profile" className="block px-2 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 rounded-lg">{t('suite.account')}</a>
              <button onClick={handleLogout} className="w-full text-left px-2 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg">{t('suite.logout')}</button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
